import React, { useState } from 'react';
import { ContactOrganizer } from '@eventbrite/contact-organizer';
import { Button } from '@eventbrite/marmalade';

/**
 * Thin trigger: a button that opens the ported ContactOrganizer modal.
 *
 * Ported from eb-ui `packages/attendee/contact-organizer-button`. eb-ui used an
 * `eds-button` with `style=link` (`STYLE_LINK`); the EDS→Marmalade mapping is
 * Marmalade `Button` with `variant="ghost"` (the link-like control style).
 * Analytics (`ContactOrgAttempt`) is forwarded via the local Heap shim.
 */

export const GA_ACTION_CONTACT_ORG_ATTEMPT = 'ContactOrgAttempt';

interface HeapWindow extends Window {
    heap?: {
        track: (event: string, properties?: Record<string, unknown>) => void;
    };
}

export const trackContactAction = (
    label: string | undefined,
    category: string,
): void => {
    try {
        if (typeof window === 'undefined') {
            return;
        }
        const { heap } = window as HeapWindow;
        if (heap && typeof heap.track === 'function') {
            heap.track(GA_ACTION_CONTACT_ORG_ATTEMPT, { category, label });
        }
    } catch {
        // Analytics must never break the button.
    }
};

export interface ContactOrganizerProps {
    organizerId: string;
    organizerName?: string;
    isVisible: boolean;
    captchaKey: string;
    onClose?: () => void;
    isAuthenticated?: boolean;
    domNodeId?: string;
    domNode?: Element;
    eventName?: string;
    /** Property required to render ContactOrganizer from Listings. */
    eventId?: string;
    collectionId?: string;
    gaCategory: string;
    /** Marmalade Button variant (defaults to the link-like 'ghost'). */
    buttonVariant?: 'primary' | 'secondary' | 'ghost';
    className?: string;
    customText?: string;
    /** Same-origin BFF path for the contact POST (threaded to ContactOrganizer). */
    bffContactPath?: string;
    /** Override the BFF path used to fetch the current user for prefill. */
    userPath?: string;
    /** Callback executed when the button is clicked. */
    onClickCallback?: () => void;
}

const isEmpty = (value?: string): boolean => !value || value.trim().length === 0;

const getContactText = (props: ContactOrganizerProps): string => {
    if (props.customText) {
        return props.customText;
    }
    if (isEmpty(props.organizerName)) {
        return 'Contact the organizer';
    }
    return 'Contact';
};

export const ContactOrganizerButton: React.FunctionComponent<
    ContactOrganizerProps
> = (props) => {
    const [isVisible, setVisible] = useState(props.isVisible);

    const contactProps = {
        ...props,
        isVisible,
        onClose: () => setVisible(false),
    };

    const contactText = getContactText(props);

    const handleClick = () => {
        trackContactAction(props.eventName, props.gaCategory);
        if (props.onClickCallback) {
            props.onClickCallback();
        }
        setVisible(true);
    };

    return (
        <>
            <Button
                variant={props.buttonVariant || 'ghost'}
                onClick={handleClick}
                data-testid="contact-organizer-button"
                className={props.className}
            >
                {contactText}
            </Button>
            {isVisible && <ContactOrganizer {...contactProps} />}
        </>
    );
};
